// src/screens/academique/FormationHome.jsx
import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  Stack,
  Chip,
  LinearProgress,
  IconButton,
  CardActions,
  Paper,
  Button
} from '@mui/material';

import {
  Book as BookIcon,
  Quiz as QuizIcon,
  EmojiEvents as EmojiEventsIcon,
  TrendingUp as TrendingUpIcon,
  ArrowForward as ArrowForwardIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Stars as StarsIcon,
  AccessTime as AccessTimeIcon,
  Bookmark as BookmarkIcon,
  Description as DescriptionIcon,
  Lock as LockIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  PictureAsPdf as PdfIcon,
  Slideshow as PptIcon,
  VideoLibrary as VideoIcon,
  InsertDriveFile as DefaultIcon
} from '@mui/icons-material';
import ImportContactsIcon from '@mui/icons-material/ImportContacts';
import { FormationContext } from './EspaceFormation';
import CoursService from '../../services/CoursService';

const FormationHome = ({ onSwitchView }) => {
  const navigate = useNavigate();
  const context = useContext(FormationContext);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 3;
  
  if (!context) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Stack spacing={2} alignItems="center">
          <LinearProgress sx={{ width: 200 }} />
          <Typography variant="body2" color="textSecondary">
            Chargement de votre espace de formation...
          </Typography>
        </Stack>
      </Box>
    );
  }

  const { student, courses, qcmResults, notificationBadge, openCourse } = context;
  
  // Fonction pour enregistrer l'historique de visualisation
  const recordHistory = async (courseId, action = 'View') => {
    try {
      const historyData = {
        cours_id: courseId,
        action: action
      };
      await CoursService.createHistory(historyData);
    } catch (error) {
      console.error('Erreur lors de l\'enregistrement de l\'historique:', error);
    }
  };
  
  // Fonction modifiée pour ouvrir un cours avec historique et navigation
  const handleOpenCourse = async (course) => {
   
    await recordHistory(course.id, 'View');

     if (openCourse) {
      openCourse(course);
     }
    
  };
  
  // Fonction pour gérer la navigation depuis les statistiques
  const handleStatClick = (path) => {
    navigate(path);
  };
  
  // Calcul du prénom à partir des données utilisateur
  const getStudentFirstName = () => {
    if (!student) return 'Candidat';
    if (student.first_name) return student.first_name;
    if (student.prenom) return student.prenom;
    if (student.name) {
      const firstName = student.name.split(' ')[0];
      return firstName;
    }
    return 'Candidat';
  };
  
  const getStudentFullName = () => {
    if (!student) return 'Candidat';
    if (student.first_name && student.last_name) {
      return `${student.first_name} ${student.last_name}`;
    }
    if (student.first_name) return student.first_name;
    if (student.name) return student.name;
    if (student.prenom && student.nom) return `${student.prenom} ${student.nom}`;
    return 'Candidat';
  };
  
  // Calcul des statistiques
  const avgScore = qcmResults && qcmResults.length 
    ? Math.round(qcmResults.reduce((a, r) => a + (r.score || 0), 0) / qcmResults.length) 
    : 0;
  
  const coursesCount = courses?.length || 0;
  const qcmResultsCount = qcmResults?.length || 0;
  const badgeCount = notificationBadge || 0;
  
  // Grouper les cours par matière
  const groupedCourses = courses && courses.length ? Object.entries(
    courses.reduce((acc, course) => {
      const matiereNom = course.matiere_nom || course.matiere?.nom || 'Sans matière';
      if (!acc[matiereNom]) acc[matiereNom] = [];
      acc[matiereNom].push(course);
      return acc;
    }, {})
  ) : [];
  
  // Pagination des matières
  const totalPages = Math.ceil(groupedCourses.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, groupedCourses.length);
  const currentMatieres = groupedCourses.slice(startIndex, startIndex + itemsPerPage);
  
  // Couleurs pour les en-têtes des cours
  const courseColors = [
    '#E3F2FD', '#E8F5E9', '#FFF3E0', '#FCE4EC', '#F3E5F5', '#E0F2F1', '#FFEBEE', '#E8EAF6'
  ];
  
  // Fonction pour obtenir le type normalisé
  const getNormalizedType = (type) => {
    const typeLower = type?.toLowerCase() || '';
    if (typeLower === 'pdf') return 'pdf';
    if (typeLower === 'ppt' || typeLower === 'pptx') return 'ppt';
    if (typeLower === 'mp4' || typeLower === 'video') return 'video';
    return 'default';
  };
  
  // Icônes selon le type de fichier
  const getFileIcon = (type) => {
    const normalizedType = getNormalizedType(type);
    switch(normalizedType) {
      case 'pdf':
        return <PdfIcon sx={{ fontSize: 40, color: '#D32F2F' }} />;
      case 'ppt':
        return <PptIcon sx={{ fontSize: 40, color: '#ED6C02' }} />;
      case 'video':
        return <VideoIcon sx={{ fontSize: 40, color: '#2E7D32' }} />;
      default:
        return <DescriptionIcon sx={{ fontSize: 40, color: '#1976D2' }} />;
    }
  };
  
  // Label selon le type de fichier
  const getFileLabel = (type) => {
    const normalizedType = getNormalizedType(type);
    switch(normalizedType) {
      case 'pdf':
        return 'PDF';
      case 'ppt':
        return 'PPT';
      case 'video':
        return 'VIDEO';
      default:
        return 'DOC';
    }
  };
  
  const stats = [
    {
      label: 'Cours disponibles',
      value: coursesCount,
      icon: <ImportContactsIcon />,
      color: '#1976D2',
      bgColor: '#E3F2FD',
      path: '/formation/cours'
    },
    {
      label: 'QCM complétés',
      value: qcmResultsCount,
      icon: <QuizIcon />,
      color: '#2E7D32',
      bgColor: '#E8F5E9',
    //  path: '/formation/qcm'
    },
    {
      label: 'Résultats',
      value: badgeCount,
      icon: <EmojiEventsIcon />,
      color: '#ED6C02',
      bgColor: '#FFF3E0',
      //path: '/formation/resultats'
    },
    {
      label: 'Note moyenne',
      value: avgScore || '?',
      suffix: avgScore ? '%' : '',
      icon: <TrendingUpIcon />,
      color: '#9C27B0',
      bgColor: '#F3E5F5',
     // path: '/formation/resultats'
    }
  ];

  // Fonction pour obtenir le résultat d'un cours
  const getCourseResult = (courseId) => {
    return qcmResults?.find(r => r.courseId === courseId || r.cours_id === courseId);
  };

  const getScoreColor = (score) => {
    return score >= 50 ? '#2E7D32' : '#D32F2F';
  };

  const getScoreBgColor = (score) => {
    return score >= 50 ? '#E8F5E9' : '#FFEBEE';
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Vérifier si un cours est verrouillé (si prérequis non complété)
  const isCourseLocked = (course) => {
    if (!course.prerequisite_course_id) return false;
    if (!qcmResults) return true;
    const prereqResult = qcmResults.find(r => 
      (r.courseId === course.prerequisite_course_id || r.cours_id === course.prerequisite_course_id)
    );
    if (!prereqResult) return true;
    const minScore = course.min_prereq_score || 50;
    return prereqResult.score < minScore;
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1976D2 0%, #0288D1 100%)',
          borderRadius: '20px',
          p: 4,
          mb: 4,
          position: 'relative',
          overflow: 'hidden',
          color: '#FFFFFF',
          boxShadow: '0 4px 20px rgba(25,118,210,0.25)',
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" position="relative" zIndex={1}>
          <Box>
            <Typography variant="h4" fontWeight="800" gutterBottom>
              Bonjour, {getStudentFirstName()} 👋
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Prêt à apprendre aujourd'hui ?
            </Typography>
          </Box>
          <StarsIcon sx={{ fontSize: 64, opacity: 0.8 }} />
        </Box>
      </Box>
      
      {/* Statistiques */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card
              sx={{
                height: '100%',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                borderRadius: '20px',
                p: 1.5,
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: 4
                }
              }}
              onClick={() => handleStatClick(stat.path)}
            >
              <CardContent sx={{ p: 0 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar
                    sx={{
                      width: 56,
                      height: 56,
                      bgcolor: stat.bgColor,
                      color: stat.color
                    }}
                  >
                    {stat.icon}
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {stat.value}{stat.suffix || ''}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stat.label}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      
      {/* En-tête des cours récents avec pagination en haut */}
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h6" fontWeight="bold" display="flex" alignItems="center" gap={1}>
          <AccessTimeIcon sx={{ color: '#1976D2' }} />
          Cours par matière
        </Typography>
        
        <Box display="flex" alignItems="center" gap={2}>
          {/* Pagination en haut */}
          {totalPages > 1 && (
            <>
              <IconButton
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                size="small"
                sx={{
                  bgcolor: currentPage === 1 ? '#F5F5F5' : '#1976D2',
                  color: currentPage === 1 ? '#9E9E9E' : 'white',
                  '&:hover': {
                    bgcolor: currentPage === 1 ? '#F5F5F5' : '#1565C0'
                  }
                }}
              >
                <ChevronLeftIcon />
              </IconButton>
              
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Matières {startIndex + 1}-{endIndex} sur {groupedCourses.length}
              </Typography>
              
              <IconButton
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                size="small"
                sx={{
                  bgcolor: currentPage === totalPages ? '#F5F5F5' : '#1976D2',
                  color: currentPage === totalPages ? '#9E9E9E' : 'white',
                  '&:hover': {
                    bgcolor: currentPage === totalPages ? '#F5F5F5' : '#1565C0'
                  }
                }}
              >
                <ChevronRightIcon />
              </IconButton>
            </>
          )}
        </Box>
      </Box>

      {/* Liste des matières */}
      {currentMatieres.length === 0 ? (
        <Box sx={{ textAlign: 'center', p: 5, borderRadius: 3, bgcolor: '#F5F5F5' }}>
          <BookIcon sx={{ fontSize: 64, color: '#9E9E9E', opacity: 0.5, mb: 2 }} />
          <Typography color="text.secondary">
            Aucun cours disponible pour le moment
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {currentMatieres.map(([matiere, coursListe]) => (
            <Grid item xs={12} md={6} lg={4} key={matiere}>
              <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Paper
                  sx={{
                    p: 2.5,
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    mb: 2
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ width: '100%' }} 
                  >
                    <Stack direction="row" spacing={2} alignItems="center">
                      <BookmarkIcon sx={{ color: '#1976D2' }} />
                      <Typography fontWeight="bold">
                        {matiere}
                      </Typography>
                    </Stack>
                    <Chip
                      label={`${coursListe.length} cours`}
                      size="small"
                      color="primary"
                    />
                  </Stack>
                </Paper>
                
                {/* Liste des cours */}
                <Box sx={{ flexGrow: 1 }}>
                  <Stack spacing={2}>
                    {coursListe.map((course, idx) => {
                      const colorIndex = idx % courseColors.length;
                      const result = getCourseResult(course.id);
                      const isLocked = isCourseLocked(course);
                      const fileType = course.type || course.file_type || 'default';
                      const fileIcon = getFileIcon(fileType);
                      const fileLabel = getFileLabel(fileType);
                      const courseTitle = course.titre || course.title || course.nom || `Cours ${idx + 1}`;
                      const courseSummary = course.summary || course.description || '';
                      
                      return (
                        <Card
                          key={course.id || idx}
                          sx={{
                            height: '100%',
                            display: 'flex',
                            borderRadius: '15px',
                            flexDirection: 'column',
                            transition: 'all 0.3s ease',
                            backgroundColor: '#FFFFFF',
                            '&:hover': {
                              transform: 'translateY(-4px)',
                              boxShadow: 4
                            }
                          }}
                        >
                          <Box
                            sx={{
                              height: 100,
                              bgcolor: courseColors[colorIndex],
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              position: 'relative',
                              borderBottom: '1px solid',
                              borderColor: 'divider'
                            }}
                          >
                            {fileIcon}
                            {isLocked && (
                              <Chip
                                icon={<LockIcon />}
                                label="Verrouillé"
                                size="small"
                                sx={{
                                  position: 'absolute',
                                  top: 8,
                                  right: 8,
                                  bgcolor: '#D32F2F',
                                  color: '#FFFFFF',
                                  '& .MuiChip-icon': { color: '#FFFFFF' }
                                }}
                              />
                            )}
                            {/* Badge du type de fichier */}
                            <Chip
                              label={fileLabel}
                              size="small"
                              sx={{
                                position: 'absolute',
                                bottom: 8,
                                right: 8,
                                bgcolor: 'rgba(0,0,0,0.7)',
                                color: '#FFFFFF',
                                fontWeight: 'bold',
                                fontSize: '0.7rem',
                                letterSpacing: '0.5px'
                              }}
                            />
                          </Box>
                          
                          <CardContent sx={{ flexGrow: 1, p: 2 }}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
                              <Chip
                                label={course.matiere_nom || course.matiere?.nom || 'Sans matière'}
                                size="small"
                                sx={{ bgcolor: '#E3F2FD', color: '#1976D2', fontWeight: 600 }}
                              />
                              {result && (
                                <Chip
                                  icon={result.score >= 50 ? <CheckCircleIcon /> : <CancelIcon />}
                                  label={`${result.score}%`}
                                  size="small"
                                  sx={{
                                    bgcolor: getScoreBgColor(result.score),
                                    color: getScoreColor(result.score),
                                    fontWeight: 600
                                  }}
                                />
                              )}
                            </Stack>
                            
                            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                              {courseTitle}
                            </Typography>
                            
                            {courseSummary && (
                              <Typography variant="body2" color="textSecondary" sx={{ lineHeight: 1.5 }}>
                                {courseSummary.length > 80 
                                  ? `${courseSummary.substring(0, 80)}...` 
                                  : courseSummary}
                              </Typography>
                            )}
                            
                            {/* Afficher le prérequis si le cours est verrouillé */}
                            {isLocked && course.prerequisite_course && (
                              <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                                🔒 Prérequis: {course.prerequisite_course.title || 'Cours précédent'} ({course.min_prereq_score || 50}% requis)
                              </Typography>
                            )}
                          </CardContent>
                          
                          <CardActions sx={{ p: 2, pt: 0 }}>
                            <Button
                              variant={isLocked ? "outlined" : "contained"}
                              size="large"
                              fullWidth
                              startIcon={isLocked ? <LockIcon /> : <VisibilityIcon />}
                              onClick={() => handleOpenCourse(course)}
                              disabled={isLocked}
                              sx={{
                                minHeight: 52, 
                                borderRadius: "12px",
                                textTransform: "none",
                                fontWeight: 600,
                                fontSize: "0.95rem",
                                py: 1.5,
                              }}
                            >
                              {isLocked ? "Cours verrouillé" : "Voir la leçon"}
                            </Button>
                          </CardActions>
                        </Card>
                      );
                    })}
                  </Stack>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
};

export default FormationHome;